import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchPackages,
  fetchCategories,
  fetchSubCategoriesForList,
  deletePackage,
} from "../../components/Manual_List/fetch/slices";
import { RootState } from "../../components/Manual_List/fetch/store";
import { FaTrash } from "react-icons/fa";
import { Package, Category, SubCategory, Language } from "../../Types/types";
import { addpackagetranslation } from "./addpackagetranslation";
import { useLanguage } from "../../components/LanguageContext";
import { useAppDispatch } from "../../components/Manual_List/fetch/hook";
import { useNavigate } from "react-router-dom";
import { MdEdit } from "react-icons/md";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CustomPagination from "../../components/Manual_List/Pagination";
import ItemsPerPageSelect from "../../components/Manual_List/ItemsPerPage";
import { SelectChangeEvent } from "@mui/material/Select";
import translationManualList from "../../components/Manual_List/translationManualList";
import SearchIcon from "@mui/icons-material/Search";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { ConfirmationModal } from "../../components/shared/ConfrimationModal";
import CircularProgress from "@mui/material/CircularProgress";
import { categoryNameToIdMap } from "../../components/Mappers/subcategoryToCategory";
import { getLabel } from "../../utils/getLabel";
import { AddPackageModal } from "../../components/shared/AddPackageModal";
import { GeneralTranslation } from "../../Translations/GeneralTranslation";

const PackagesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const packages: Package[] = useSelector(
    (state: RootState) => state.data.packages.data
  );
  const categories: Category[] = useSelector(
    (state: RootState) => state.data.categories.data
  );
  const subCategories: SubCategory[] = useSelector(
    (state: RootState) => state.data.fetchSubCategoriesForListStatus.data
  );
  const totalPackages: number = useSelector(
    (state: RootState) => state.data.totalPackages
  );

  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAddPackageModalOpen, setIsAddPackageModalOpen] = useState(false);
  const [translations, setTranslations] = useState({
    save: "Save",
    discard: "Discard",
    addPackage: "Add package",
  });
  const [values, setValues] = useState({});

  const { language } = useLanguage();
  const t = addpackagetranslation[language as Language];
  const ml = translationManualList[language as Language];
  const gt = GeneralTranslation[language as Language];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const categoryNameToIdMapResult = categoryNameToIdMap(categories);

  useEffect(() => {
    dispatch(fetchCategories(language));
  }, [dispatch, language]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      await dispatch(
        fetchPackages({
          language,
          categoryIds: categoryFilter ? [categoryFilter] : [],
          subCategoryIds: subcategoryFilter ? [subcategoryFilter] : [],
          page: page - 1,
          count: itemsPerPage,
          searchQuery: searchTerm,
        })
      );
      setLoading(false);
    }
    fetchData();
  }, [
    dispatch,
    language,
    page,
    itemsPerPage,
    categoryFilter,
    subcategoryFilter,
    searchTerm,
  ]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleCategoryChange = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const categoryId = event.target.value;
    setCategoryFilter(categoryId);
    setSubcategoryFilter("");
    setPage(1);

    if (categoryId) {
      await dispatch(
        fetchSubCategoriesForList({ language, categoryId: Number(categoryId) })
      );
    }
  };

  const handleSubcategoryChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSubcategoryFilter(event.target.value);
    setPage(1);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleDeletePackage = (id: number) => {
    dispatch(deletePackage(id))
      .then((response: any) => {
        if (response.error) {
          toast.error(gt.deleteFailed);
        } else {
          toast.success(gt.deleteSuccess);
        }
      })
      .finally(() => {
        setIsConfirmationModalOpen(false);
      });
  };

  const openConfirmationModal = (pkg: Package) => {
    setPackageToDelete(pkg);
    setIsConfirmationModalOpen(true);
  };

  const openAddPackageModal = () => {
    setIsAddPackageModalOpen(true);
  };

  const closeAddPackageModal = () => {
    setIsAddPackageModalOpen(false);
  };

  const pageCount = Math.ceil(totalPackages / itemsPerPage);

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow relative">
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-9xl flex flex-col">
          <div
            className={`flex ${
              isSmallScreen || isMediumScreen
                ? "flex-col items-center"
                : "justify-between"
            } items-center mb-4`}
          >
            <h1
              className={`${
                isSmallScreen ? "text-lg" : "text-xl"
              } font-semibold`}
            >
              {t.title}
            </h1>

            <div
              className={`flex ${
                isSmallScreen || isMediumScreen
                  ? "flex-col space-y-2 items-center w-full"
                  : "space-x-4"
              }`}
            >
              <select
                className="px-3 py-2 border rounded-md w-64 outline-none dark:bg-darkBackground dark:border-purple"
                value={categoryFilter}
                onChange={handleCategoryChange}
              >
                <option value="">{t.category}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id.toString()}>
                    {category.name}
                  </option>
                ))}
              </select>
              <select
                className="px-3 py-2 border rounded-md w-64 outline-none dark:bg-darkBackground dark:border-purple"
                value={subcategoryFilter}
                onChange={handleSubcategoryChange}
                disabled={!categoryFilter}
              >
                <option value="">{t.subcategory}</option>
                {subCategories.map((subCategory) => (
                  <option key={subCategory.id} value={subCategory.id}>
                    {subCategory.name}
                  </option>
                ))}
              </select>
              <div className="relative w-64">
                <input
                  type="text"
                  placeholder={ml.search}
                  className="px-3 py-2 border rounded-md w-full pr-10 outline-none dark:bg-darkBackground dark:border-purple"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <SearchIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 dark:text-white" />
              </div>
            </div>
          </div>
          <div
            className={`flex ${
              isSmallScreen || isMediumScreen
                ? "justify-center mb-4"
                : "justify-end mb-4"
            }`}
          >
            <button
              className="bg-purple text-white px-4 py-2 rounded font-medium shadow-sm"
              onClick={openAddPackageModal}
            >
              {t.addPackage}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-4 font-medium text-left ">{t.name}</th>
                  <th className="py-2 px-4 font-medium text-left">
                    {t.category}
                  </th>
                  <th className="py-2 px-4 font-medium text-left">
                    {t.subcategory}
                  </th>
                  <th className="py-2 px-4 font-medium text-left"></th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg, index) => (
                  <tr
                    key={pkg.id}
                    className={`${
                      index !== packages.length - 1
                        ? "border-b border-gray-300 dark:bg-darkSecondaryBackground"
                        : ""
                    }`}
                  >
                    <td
                      className={`py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground ${
                        index === 0 ? "rounded-tl-md" : ""
                      } ${
                        index === packages.length - 1 ? "rounded-bl-md" : ""
                      }`}
                    >
                      {getLabel(pkg.label, language)}
                    </td>
                    <td className="py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground">
                      {pkg.category}
                    </td>
                    <td className="py-2 px-4 text-left font-extralight bg-white dark:bg-darkSecondaryBackground">
                      {pkg.subCategory}
                    </td>
                    <td
                      className={`py-2 px-4 text-right flex font-extralight space-x-2 items-center bg-white dark:bg-darkSecondaryBackground ${
                        index === 0 ? "rounded-tr-md" : ""
                      } ${
                        index === packages.length - 1 ? "rounded-br-md" : ""
                      }`}
                    >
                      <MdEdit
                        className="text-black ml-auto cursor-pointer dark:text-white"
                        size={24}
                        onClick={() => navigate(`/add-package/${pkg.id}`)}
                      />
                      <FaTrash
                        className="text-red_1 cursor-pointer"
                        size={20}
                        onClick={() => openConfirmationModal(pkg)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {packages.length === 0 && (
            <div className="text-center my-4 col-span-full">
              <p className="text-lg text-gray-500">
                No data matches this category.
              </p>
            </div>
          )}
          <div className="flex justify-between items-center pt-6 w-full">
            <CustomPagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
            />
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              handleItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
        {loading && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
            <CircularProgress />
          </div>
        )}
      </div>
      {packageToDelete && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={() => handleDeletePackage(packageToDelete.id)}
          label={t.deleteConfirmation.replace("{label}", packageToDelete.label)}
        />
      )}
      {isAddPackageModalOpen && (
        <AddPackageModal
          isOpen={isAddPackageModalOpen}
          onClose={closeAddPackageModal}
          translations={gt}
          values={values}
          setValues={setValues}
          onClick={() => navigate("/add-package")}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default PackagesList;
